import Link from 'next/link'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import { Field, Form, TextArea, TextInput } from '../../components/Form'
import { Col, Row } from '../../components/Layout/FlexGrid'
import MaxWidth from '../../components/Layout/MaxWidth'
import NextSeo from '../../components/NextSeo'
import Button from '../../containers/Button'
import { useTracking } from '../../hooks'
import { useTranslation } from '../../i18n'
import ContactUsDesktopSvg from '../../public/static/images/corporate/corporate-contact-us-desktop.svg'
import ContactUsMobileSvg from '../../public/static/images/corporate/corporate-contact-us-mobile.svg'
import { sendContactFormRequestAsync } from '../../redux/corporate/actions'
import { getColor, getFontSize } from '../../utils/cssUtils'
import {
  corporateContactSubmitForm,
  corporateContactSubmitSuccess,
  corporateContactSuccessClickBack,
  corporateContactView
} from '../../utils/dataTracker'
import mq from '../../utils/mediaQuery'
import { emailRegex, phoneRegex } from '../../utils/validator'

const Wrapper = styled(MaxWidth)`
  width: 100%;
`
const StyledRow = styled(Row)`
  padding: 24px 0;
`
const KeyVisualDesktop = styled(ContactUsDesktopSvg)`
  width: 100%;
  ${mq.tabletMobile} {
    display: none;
  }
`
const KeyVisualMobile = styled(ContactUsMobileSvg)`
  width: 100%;
  ${mq.desktop} {
    display: none;
  }
`
const Title = styled.h1`
  color: ${getColor('black')};
  font-size: ${getFontSize('title')};
`
const Description = styled.p`
  color: ${getColor('grey')};
  font-size: ${getFontSize('body2')};
`
const StyledTextArea = styled(TextArea)`
  height: 96px;
`

const Contact = props => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const track = useTracking()
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false)
  const formOptions = {
    submitFocusError: false
  }
  const onSubmit = async (data, e) => {
    e.preventDefault()
    setIsLoading(true)
    track(corporateContactSubmitForm())
    try {
      await dispatch(sendContactFormRequestAsync(data))
      setIsLoading(false)
      setIsSubmitSuccess(true)
      track(corporateContactSubmitSuccess())
    } catch (e) {
      setIsLoading(false)
    }
  }

  const handleBack = useCallback(e => {
    track(corporateContactSuccessClickBack())
  }, [])

  useEffect(() => {
    track(corporateContactView(), true)
  }, [])

  return (
    <Wrapper>
      <NextSeo
        title={t('seo.corporate.contact.ogTitle')}
        description={t('seo.corporate.contact.ogDescription')}
        image='/static/images/og-corporate-contact.png'
      />
      <StyledRow gutter={[24, 24]}>
        <Col md={24} xl={12}>
          <KeyVisualDesktop />
          <KeyVisualMobile />
        </Col>
        {isSubmitSuccess ? (
          <Col md={24} xl={12}>
            <Title>{t('corporate.contactUs.submitSuccessTitle')}</Title>
            <Description
              dangerouslySetInnerHTML={{
                __html: t('corporate.contactUs.submitSuccessDescription')
              }}
            />
            <Link href='/corporate'>
              <a onClick={handleBack}>
                <Button height={48}>
                  {t('corporate.contactUs.backToCorporateHome')}
                </Button>
              </a>
            </Link>
          </Col>
        ) : (
          <Col md={24} xl={12}>
            <Title>{t('corporate.contactUs.headline')}</Title>
            <Description>{t('corporate.contactUs.description')}</Description>
            <Form options={formOptions} onSubmit={onSubmit} noValidate>
              {methods => (
                <>
                  <Row gutter={[24, 48]}>
                    <Col>
                      <Field
                        name='corporateName'
                        label={t('corporate.contactUs.corporateName')}
                        validationRules={{
                          required: t('corporate.contactUs.requiredField')
                        }}
                      >
                        <TextInput />
                      </Field>
                    </Col>
                    <Col xl={12}>
                      <Field
                        name='name'
                        label={t('corporate.contactUs.name')}
                        validationRules={{
                          required: t('corporate.contactUs.requiredField')
                        }}
                      >
                        <TextInput />
                      </Field>
                    </Col>
                    <Col xl={12}>
                      <Field
                        name='title'
                        label={t('corporate.contactUs.title')}
                        validationRules={{
                          required: t('corporate.contactUs.requiredField')
                        }}
                      >
                        <TextInput />
                      </Field>
                    </Col>
                    <Col>
                      <Field
                        name='phoneNo'
                        label={t('corporate.contactUs.phoneNo')}
                        validationRules={{
                          required: t('corporate.contactUs.requiredField'),
                          pattern: {
                            value: phoneRegex,
                            message: t('corporate.contactUs.invalidInput')
                          }
                        }}
                      >
                        <TextInput type='tel' />
                      </Field>
                    </Col>
                    <Col>
                      <Field
                        name='email'
                        label={t('corporate.contactUs.email')}
                        validationRules={{
                          required: t('corporate.contactUs.requiredField'),
                          pattern: {
                            value: emailRegex,
                            message: t('corporate.contactUs.invalidInput')
                          }
                        }}
                      >
                        <TextInput type='email' />
                      </Field>
                    </Col>
                    <Col>
                      <Field
                        name='remark'
                        label={t('corporate.contactUs.remark')}
                      >
                        <StyledTextArea />
                      </Field>
                    </Col>
                  </Row>
                  <Button
                    height={48}
                    css={`
                      width: 150px;
                      margin-top: 24px;
                    `}
                    loading={isLoading}
                    type='submit'
                  >
                    {t('corporate.contactUs.submit')}
                  </Button>
                </>
              )}
            </Form>
          </Col>
        )}
      </StyledRow>
    </Wrapper>
  )
}

export default Contact
